import React, { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";
import { isAdmin, isLimitedAdmin } from "../../lib/roleUtils";



// TanStack Query hooks
import { useCustomers } from "../../queries/customerQueries";
import { useSavePackingList, useUpdatePackingList } from "../../mutations/packingListMutations";

// Step components from separate files
import Step1Details from "./Step1Details";
import Step2Addresses from "./Step2Addresses";
import Step3TeakRounds from "./Step3TeakRounds";
import Step4TeakSwans from "./Step4TeakSwans";
import PageHeader from "../common/PageHeader/PageHeader";

// ─── Main Component ──────────────────────────────────────────────────────────
export default function NewPackingList(props) {
    const { control, handleSubmit, watch, setValue, reset } = useForm({
        defaultValues: {
            // ── String fields ──────────────────────────────────────────────
            fileId: "",
            plDate: null,
            agentName: "",
            partyName: "",
            importer: "",
            pkl: "",
            load: "",
            container: "",
            lorry: "",
            shipTo: "",
            pincode: "",
            fromName: "",
            fromNumber: "",
            fromStreet: "",
            fromLandmark: "",
            fromCity: "",
            fromDistrict: "",
            fromPincode: "",
            toName: "",
            toNumber: "",
            toStreet: "",
            toLandmark: "",
            toCity: "",
            toDistrict: "",
            toPincode: "",
            gstNumber: "",
            girthAction: "PLUS",
            item: "TEAK ROUND LOGS",

            // ── Number fields ──────────────────────────────────────────────
            cbm: 0,
            comm: 0,
            received: 0,
            paidAmount: 0,
            balanceAmount: 0,
            rate: 0,
            mts: 0,
            distance: 0,
            avgLength: 0,
            avgGirth: 0,
            totalCbm: 0,
            totalNos: 0,
            avgCft: 0,
            swanNos: 0,
            swanCbm: 0,
            totalTeakNos: 0,
            totalTeakCbm: 0,
            amount: 0,
            sumMTS: 0,
            invCBM: 0,
            invRate: 0,
            invAmount: 0,
            newValue: 0,
            averageModifyGirth: 0,
            modifyTotalCBM: 0,
            mtsNewValue: 0,

            // ── Boolean fields ─────────────────────────────────────────────
            plusGst: false,
            editModifySwitch: false,

            // ── Array fields ───────────────────────────────────────────────
            newTeakRounds: [
                { id: 0, length: "", girth: "", cbm: "", modifyGirth: "", modifyCbm: "" },
            ],
            newTeakSwans: [
                { id: 0, width: "", thickness: "", length: "", noOfPieces: "", cbm: "" },
            ],
        },
    });

    const {
        fields: userFields,
        append: appendUser,
        remove: removeUser,
        replace: replaceUser,
    } = useFieldArray({
        control,
        name: "newTeakRounds",
    });

    const {
        fields: teakFields,
        append: appendTeak,
        remove: removeTeak,
        replace: replaceTeak,
    } = useFieldArray({
        control,
        name: "newTeakSwans",
    });

    const navigate = useNavigate();
    const { state } = useLocation();
    const isEditMode = !!(state && (state.id || state._id || state.fileId));

    const role = sessionStorage.getItem("userRole") || localStorage.getItem("userRole") || "superAdmin";
    const isUserAdmin = isAdmin(role) || isLimitedAdmin(role);

    const [showModifyGirth, setShowModifyGirth] = useState(false);

    const formData = watch();

    // RESTORE STATE OR LOCALSTORAGE DATA
    useEffect(() => {
        if (state && state?.id) {
            setValue("fileId", state?.fileId);
            setValue("plDate", state?.plDate);
            setValue("agentName", state?.agentName);
            setValue("partyName", state?.partyName);
            setValue("importer", state?.importer);
            setValue("pkl", state?.pkl);
            setValue("cbm", state?.cbm);
            setValue("comm", state?.comm);
            setValue("load", state?.load);
            setValue("received", state?.received);
            setValue("paidAmount", state?.paidAmount);
            setValue("balanceAmount", state?.balanceAmount);
            setValue("rate", state?.rate);
            setValue("mts", state?.mts);
            setValue("container", state?.container);
            setValue("lorry", state?.lorry);
            setValue("distance", state?.distance);
            setValue("shipTo", state?.shipTo);
            setValue("pincode", state?.pincode);
            setValue("fromName", state?.fromName);
            setValue("fromNumber", state?.fromNumber);
            setValue("fromStreet", state?.fromStreet);
            setValue("fromLandmark", state?.fromLandmark);
            setValue("fromCity", state?.fromCity);
            setValue("fromDistrict", state?.fromDistrict);
            setValue("fromPincode", state?.fromPincode);
            setValue("toName", state?.toName);
            setValue("toNumber", state?.toNumber);
            setValue("toStreet", state?.toStreet);
            setValue("toLandmark", state?.toLandmark);
            setValue("toCity", state?.toCity);
            setValue("toDistrict", state?.toDistrict);
            setValue("toPincode", state?.toPincode);
            setValue("gstNumber", state?.gstNumber);
            setValue("avgLength", state?.avgLength);
            setValue("avgGirth", state?.avgGirth);
            setValue("totalCbm", state?.totalCbm);
            setValue("totalNos", state?.totalNos);
            setValue("avgCft", state?.avgCft);
            setValue("swanNos", state?.swanNos);
            setValue("swanCbm", state?.swanCbm);
            setValue("totalTeakNos", state?.totalTeakNos);
            setValue("totalTeakCbm", state?.totalTeakCbm);
            setValue("amount", state?.amount);
            setValue("sumMTS", state?.sumMTS);
            setValue("invCBM", state?.invCBM);
            setValue("invRate", state?.invRate);
            setValue("item", state?.item);
            setValue("invAmount", state?.invAmount);
            setValue("plusGst", state?.plusGst);
            setValue("modifyTotalCBM", state?.modifyTotalCBM);
            setValue("averageModifyGirth", state?.averageModifyGirth);
            setValue("girthAction", state?.girthAction);
            setValue("newValue", state?.newValue);
            setValue("editModifySwitch", state?.editModifySwitch);
            setValue("mtsNewValue", state?.mtsNewValue);

            if (state?.newTeakRounds?.length > 0) {
                replaceUser(
                    state?.newTeakRounds?.map((item) => ({
                        length: item?.length || "",
                        girth: item.girth || "",
                        cbm: item.cbm || "",
                        modifyGirth: item.modifyGirth || "",
                        modifyCbm: item.modifyCbm || "",
                    }))
                );
            }
            if (state?.newTeakSwans?.length > 0) {
                replaceTeak(
                    state?.newTeakSwans?.map((item) => ({
                        id: item?.id || 0,
                        width: item?.width || "",
                        thickness: item?.thickness || "",
                        length: item?.length || "",
                        noOfPieces: item?.noOfPieces || "",
                        cbm: item?.cbm || "",
                    }))
                );
            }
        } else {
            const savedFormData = localStorage.getItem("formData");
            if (savedFormData) {
                const parsedData = JSON.parse(savedFormData);
                Object.keys(parsedData)?.forEach((fieldName) => {
                    if (fieldName !== "newTeakRounds" && fieldName !== "newTeakSwans") {
                        setValue(fieldName, parsedData[fieldName]);
                    }
                });
                if (parsedData?.newTeakRounds?.length > 0) {
                    replaceUser(
                        parsedData.newTeakRounds.map((item) => ({
                            length: item?.length || "",
                            girth: item?.girth || "",
                            cbm: item?.cbm || "",
                            modifyGirth: item?.modifyGirth || "",
                            modifyCbm: item?.modifyCbm || "",
                        }))
                    );
                }
                if (parsedData?.newTeakSwans?.length > 0) {
                    replaceTeak(
                        parsedData.newTeakSwans.map((item) => ({
                            id: item?.id || 0,
                            width: item?.width || "",
                            thickness: item?.thickness || "",
                            length: item?.length || "",
                            noOfPieces: item?.noOfPieces || "",
                            cbm: item?.cbm || "",
                        }))
                    );
                }
            } else {
                setValue("partyName", "");
                setValue("item", "TEAK ROUND LOGS");
            }
        }
    }, [state, setValue, replaceUser, replaceTeak]);

    // TanStack Query Mutations
    const saveMutation = useSavePackingList();
    const updateMutation = useUpdatePackingList();

    const isLoading = saveMutation.isPending;
    const updateLoading = updateMutation.isPending;

    // STEP CONTROL — Admin role opens directly to Step 3 (Log Entry)
    const [step, setStep] = useState(() => {
        if (isUserAdmin) {
            return 3;
        }
        return 1;
    });

    // Clear step state and form data from localStorage on unmount (navigation to another page)
    useEffect(() => {
        return () => {
            localStorage.removeItem("currentStep");
            localStorage.removeItem("formData");
        };
    }, []);

    // INPUT FIELD FOCUS MOVEMENT REFS & HANDLERS
    const lengthRefs = useRef([]);
    const girthRefs = useRef([]);

    const handleKeyPress = (event, index, fieldType) => {
        if (event.key === "Enter") {
            event.preventDefault();

            if (fieldType === "length") {
                girthRefs.current[index]?.focus();
            } else if (fieldType === "girth") {
                if (index === userFields?.length - 1) {
                    appendUser({
                        length: "",
                        girth: "",
                        cbm: "",
                        modifyGirth: "",
                        modifyCbm: "",
                    });
                    setTimeout(() => {
                        lengthRefs.current[index + 1]?.focus();
                    }, 0);
                } else {
                    lengthRefs.current[index + 1]?.focus();
                }
            }
        }
    };

    const fieldRefs = useRef({
        width: [],
        thickness: [],
        length: [],
        noOfPieces: [],
    });

    const handleEnterKeyPress = (event, fieldType, index) => {
        if (event.key === "Enter") {
            event.preventDefault();

            switch (fieldType) {
                case "width":
                    fieldRefs.current.thickness[index]?.focus();
                    break;
                case "thickness":
                    fieldRefs.current.length[index]?.focus();
                    break;
                case "length":
                    fieldRefs.current.noOfPieces[index]?.focus();
                    break;
                case "noOfPieces":
                    if (index === teakFields?.length - 1) {
                        appendTeak({
                            width: "",
                            thickness: "",
                            length: "",
                            noOfPieces: "",
                            cbm: "",
                        });
                        setTimeout(() => {
                            fieldRefs.current.width[index + 1]?.focus();
                        }, 0);
                    } else {
                        fieldRefs.current.width[index + 1]?.focus();
                    }
                    break;
                default:
                    break;
            }
        }
    };

    // API Query to fetch Customer Names
    const { data: apiCustomers } = useCustomers();
    const customerNameList = apiCustomers?.data || apiCustomers || [];

    const { partyName, fromName } = watch();
    const foundCustomer = customerNameList?.find(
        (customer) => customer?.customerName === fromName
    );

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [selectedShippingIndex, setSelectedShippingIndex] = useState(null);

    const handleAddressSelect = (name, address) => {
        setSelectedCategory(name);
        setSelectedAddress(address);
    };

    // Address selection effect
    useEffect(() => {
        if (selectedAddress || state?.id) {
            setValue("toName", selectedCategory || state?.toName);
            setValue("toNumber", selectedAddress?.shippingAddressDoorNo || state?.toNumber);
            setValue("toStreet", selectedAddress?.shippingAddressStreet || state?.toStreet);
            setValue("toLandmark", selectedAddress?.shippingAddressLandMark || state?.toLandmark);
            setValue("toCity", selectedAddress?.shippingAddressCity || state?.toCity);
            setValue("toDistrict", selectedAddress?.shippingAddressState || state?.toDistrict);
            setValue("toPincode", selectedAddress?.shippingAddressPincode || state?.toPincode);
            setValue("distance", selectedAddress?.kiloMeter || state?.distance);
        } else {
            setValue("toName", "");
            setValue("toNumber", "");
            setValue("toStreet", "");
            setValue("toLandmark", "");
            setValue("toCity", "");
            setValue("toDistrict", "");
            setValue("toPincode", "");
            setValue("distance", "");
        }
    }, [selectedAddress, selectedCategory, setValue, state]);

    const updateCustomerFields = (customer) => {
        const empty = "";
        setValue("fromName", customer?.customerName || empty);
        setValue("fromNumber", customer?.doorNo || empty);
        setValue("fromStreet", customer?.street || empty);
        setValue("fromLandmark", customer?.landMark || empty);
        setValue("fromCity", customer?.city || empty);
        setValue("fromDistrict", customer?.district || empty);
        setValue("fromPincode", customer?.pincode || empty);
        setValue("gstNumber", customer?.gstNumber || empty);
    };

    useEffect(() => {
        const currentStep = localStorage.getItem("currentStep");
        if (currentStep === "3" && state?.girthAction != null) {
            setShowModifyGirth(true);
        }
    }, [state?.girthAction]);

    const newTeakRounds = watch("newTeakRounds") || [];
    const girthAction = watch("girthAction") || "PLUS";
    const newValue = watch("newValue");

    // CALCULATE TEAK ROUND LOGS
    const calculateValues = () => {
        let totalLength = 0;
        let totalGirth = 0;
        let totalCbm = 0;
        let totalModifyGirth = 0;
        let totalModifyCbm = 0;

        newTeakRounds?.forEach((item, index) => {
            const length = Number(item?.length) || 0;
            const girth = Number(item?.girth) || 0;
            const cbm = length && girth ? (length * girth * girth) / 16000000 : 0;

            totalLength += length;
            totalGirth += girth;
            totalCbm += cbm;

            setValue(`newTeakRounds.${index}.cbm`, Number(cbm).toFixed(3));

            if (showModifyGirth) {
                const modifier = parseFloat(newValue) || 0;
                const modifyGirth = girthAction === "PLUS" ? girth + modifier : girth - modifier;
                const modifyCbm = length && modifyGirth ? (length * modifyGirth * modifyGirth) / 16000000 : 0;

                totalModifyGirth += modifyGirth;
                totalModifyCbm += modifyCbm;

                setValue(`newTeakRounds.${index}.modifyGirth`, Number(modifyGirth).toFixed(2));
                setValue(`newTeakRounds.${index}.modifyCbm`, Number(modifyCbm).toFixed(3));
            }
        });

        const totalNos = newTeakRounds?.length || 0;
        const avgLength = totalNos > 0 ? (totalLength / totalNos).toFixed(2) : "0.00";
        const avgGirth = totalNos > 0 ? (totalGirth / totalNos).toFixed(2) : "0.00";
        const avgCft = totalNos > 0 ? ((totalCbm * 35.315) / totalNos).toFixed(2) : "0.00";

        setValue("avgLength", Number(avgLength) || null);
        setValue("avgGirth", Number(avgGirth) || null);
        setValue("totalCbm", Number(totalCbm).toFixed(3) || null);
        setValue("avgCft", Number(avgCft) || null);
        setValue("totalNos", Number(totalNos) || 0);

        if (showModifyGirth) {
            const avgModifyGirth = totalNos > 0 ? (totalModifyGirth / totalNos).toFixed(2) : "0.00";
            setValue("averageModifyGirth", Number(avgModifyGirth) || 0);
            setValue("modifyTotalCBM", Number(totalModifyCbm).toFixed(3) || null);
        }
    };

    useEffect(() => {
        calculateValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newTeakRounds, newValue, girthAction, showModifyGirth]);

    // Account fields & calculation watch
    const {
        load,
        received,
        paidAmount,
        comm,
        rate,
        mts,
        totalCbm,
        totalTeakCbm,
        modifyTotalCBM,
        mtsNewValue,
        cbm,
        sumMTS,
    } = watch();

    // Calculate Commission
    useEffect(() => {
        const parsedTotalCbm = Number(totalCbm) || 0;
        const parsedModifyTotalCbm = Number(modifyTotalCBM) || 0;
        const parsedCbm = Number(cbm) || 0;

        if (parsedCbm === 0) {
            setValue("comm", "0.00");
            return;
        }

        const referenceCbm = Math.max(parsedTotalCbm, parsedModifyTotalCbm);
        const commission = parsedCbm * 35.315 * referenceCbm;
        setValue("comm", commission.toFixed(2));
    }, [setValue, totalCbm, modifyTotalCBM, cbm]);

    // Calculate MTS
    useEffect(() => {
        const total = Number(totalCbm || 0);
        const modified = Number(modifyTotalCBM || 0);
        const rateVal = Number(rate || 0);
        const sumMtsVal = Number(sumMTS || 0);

        if (Number(mtsNewValue) > 0) {
            if (rateVal > 0) {
                const calculatedMts = sumMtsVal / rateVal / 35.315;
                setValue("mts", calculatedMts.toFixed(3));
            } else {
                setValue("mts", 0);
            }
        } else {
            if (total > modified) {
                const differenceMts = total - modified;
                setValue("mts", differenceMts.toFixed(3));
            } else {
                setValue("mts", 0);
            }
        }
    }, [totalCbm, modifyTotalCBM, setValue, mtsNewValue, sumMTS, rate]);

    // Account details calculations
    useEffect(() => {
        const originalCbm = Number(totalCbm) || Number(totalTeakCbm) || 0;
        const modifiedCbm = Number(modifyTotalCBM) || 0;
        const finalCbm = modifiedCbm > originalCbm ? Number(modifiedCbm) : Number(originalCbm);

        const numRate = Number(rate) || 0;
        const hasMtsNewValue = Number(mtsNewValue) > 0;
        const numMts = Number(mts) || 0;
        const numLoad = Number(load) || 0;
        const numReceived = Number(received) || 0;
        const numPaidAmount = Number(paidAmount) || 0;
        const numComm = Number(comm) || 0;

        let calculatedSumMts = 0;
        if (hasMtsNewValue) {
            calculatedSumMts = Number(mtsNewValue);
            setValue("sumMTS", calculatedSumMts.toFixed(2));
        } else {
            calculatedSumMts = Number(numMts) * 35.315 * Number(numRate);
            setValue("sumMTS", Number(calculatedSumMts).toFixed(2));
        }

        const amount = Number(finalCbm) * 35.315 * Number(numRate);
        setValue("amount", Number(amount).toFixed(2));

        const invCbm = Number(finalCbm) - Number(numMts);
        setValue("invCBM", Number(invCbm).toFixed(3));

        const invAmount = Number(amount) - Number(calculatedSumMts) - Number(numLoad);
        setValue("invAmount", Number(invAmount).toFixed(2));

        const invRate = invCbm !== 0 ? Number(invAmount) / Number(invCbm) : 0;
        setValue("invRate", Number(invRate).toFixed(2));

        const balanceAmt = numLoad + calculatedSumMts - numComm - numReceived + numPaidAmount;
        setValue("balanceAmount", Number(balanceAmt).toFixed(2));
    }, [
        setValue,
        totalCbm,
        totalTeakCbm,
        modifyTotalCBM,
        load,
        received,
        paidAmount,
        comm,
        rate,
        mts,
        mtsNewValue,
    ]);

    const saveFormDataToLocalStorage = () => {
        const currentData = watch();
        if (!state) {
            localStorage.setItem("formData", JSON.stringify(currentData));
        }
    };

    const teakRoundFields = watch("newTeakSwans");
    const [aggregatedData, setAggregatedData] = useState([]);

    // CALCULATE TEAK SWAN LOGS
    const calculateAndUpdate = () => {
        const dataMap = {};

        teakRoundFields?.forEach((item, index) => {
            const { width, thickness, noOfPieces, length } = item;

            if (length && width && thickness && noOfPieces) {
                const swancbm = (Number(length) * Number(width) * Number(thickness) * Number(noOfPieces)) / 144 / 35.315;
                setValue(`newTeakSwans.${index}.cbm`, Number(swancbm).toFixed(3) || null);

                const key = `${width}X${thickness}`;
                if (!dataMap[key]) {
                    dataMap[key] = {
                        sizes: key,
                        nos: parseFloat(noOfPieces) || 0,
                        cbm: parseFloat(swancbm) || 0,
                    };
                } else {
                    dataMap[key].nos += parseFloat(noOfPieces) || 0;
                    dataMap[key].cbm += parseFloat(swancbm) || 0;
                }
            }
        });

        const aggregated = Object.values(dataMap);
        setAggregatedData(aggregated);

        const totalNos = aggregated?.reduce((sum, item) => sum + (Number(item?.nos) || 0), 0);
        setValue("totalTeakNos", totalNos || null);

        const totalTeaksCbm = teakRoundFields?.reduce((sum, item) => sum + (parseFloat(item?.cbm) || 0), 0) || 0;
        setValue("totalTeakCbm", Number(totalTeaksCbm).toFixed(3) || 0);
    };

    useEffect(() => {
        calculateAndUpdate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teakRoundFields]);

    // FORM SUBMISSION
    const onSubmit = (data) => {
        // ── Type-safe helpers ────────────────────────────────────────────────
        // Matches swagger exactly: number fields → 0 (never null), string fields → ""
        const toNum = (v) =>
            v !== "" && v !== null && v !== undefined ? Number(v) : 0;
        const toStr = (v) =>
            v !== "" && v !== null && v !== undefined ? String(v) : "";

        const payload = {
            // ── String fields ─────────────────────────────────────────────
            fileId: toStr(data?.fileId),
            plDate: data?.plDate || null,             // DateTime? — null when empty, not ""
            agentName: toStr(data?.agentName),
            partyName: toStr(data?.partyName),
            importer: toStr(data?.importer),
            pkl: toStr(data?.pkl),
            load: toStr(data?.load),
            container: toStr(data?.container),
            lorry: toStr(data?.lorry),
            shipTo: toStr(data?.shipTo),
            pincode: toStr(data?.pincode),
            fromName: toStr(data?.fromName),
            fromNumber: toStr(data?.fromNumber),
            fromStreet: toStr(data?.fromStreet),
            fromLandmark: toStr(data?.fromLandmark),
            fromCity: toStr(data?.fromCity),
            fromDistrict: toStr(data?.fromDistrict),
            fromPincode: toStr(data?.fromPincode),
            toName: toStr(data?.toName),
            toNumber: toStr(data?.toNumber),
            toStreet: toStr(data?.toStreet),
            toLandmark: toStr(data?.toLandmark),
            toCity: toStr(data?.toCity),
            toDistrict: toStr(data?.toDistrict),
            toPincode: toStr(data?.toPincode),
            gstNumber: toStr(data?.gstNumber),
            item: toStr(data?.item),
            girthAction: toStr(data?.girthAction),

            // ── Number fields ─────────────────────────────────────────────
            cbm: toNum(data?.cbm),
            comm: toNum(data?.comm),
            received: toNum(data?.received),
            paidAmount: toNum(data?.paidAmount),
            balanceAmount: toNum(data?.balanceAmount),
            rate: toNum(data?.rate),
            mts: toNum(data?.mts),
            distance: toNum(data?.distance),
            avgLength: toNum(data?.avgLength),
            avgGirth: toNum(data?.avgGirth),
            totalCbm: toNum(data?.totalCbm),
            totalNos: toNum(data?.totalNos),
            avgCft: toNum(data?.avgCft),
            swanNos: toNum(data?.swanNos),
            swanCbm: toNum(data?.swanCbm),
            totalTeakNos: toNum(data?.totalTeakNos),
            totalTeakCbm: toNum(data?.totalTeakCbm),
            amount: toNum(data?.amount),
            sumMTS: toNum(data?.sumMTS),
            invCBM: toNum(data?.invCBM),
            invRate: toNum(data?.invRate),
            invAmount: toNum(data?.invAmount),
            modifyTotalCBM: toNum(data?.modifyTotalCBM),
            averageModifyGirth: toNum(data?.averageModifyGirth),
            newValue: toNum(data?.newValue),
            mtsNewValue: toNum(data?.mtsNewValue),

            // ── Boolean fields ────────────────────────────────────────────
            plusGst: !!data?.plusGst,
            editModifySwitch: !!data?.editModifySwitch,
            isActive: true,

            // ── Array fields — sanitized to correct types ─────────────────
            // "" → null for all numeric sub-fields (fixes Decimal? 400 error)
            newTeakRounds: (data?.newTeakRounds || []).map((item) => ({
                id: Number(item?.id) || 0,
                length: toNum(item?.length),
                girth: toNum(item?.girth),
                cbm: toNum(item?.cbm),
                modifyGirth: toNum(item?.modifyGirth),   // Decimal? — must be null not ""
                modifyCbm: toNum(item?.modifyCbm),     // Decimal? — must be null not ""
            })),
            newTeakSwans: (data?.newTeakSwans || []).map((item) => ({
                id: Number(item?.id) || 0,
                width: toNum(item?.width),
                thickness: toNum(item?.thickness),
                length: toNum(item?.length),
                noOfPieces: toNum(item?.noOfPieces),
                cbm: toNum(item?.cbm),
            })),
        };


        if (state && state?.id != null) {
            updateMutation.mutate(
                { id: state.id, data: payload },
                {
                    onSuccess: () => {
                        reset();
                        localStorage.removeItem("formData");
                        localStorage.removeItem("currentStep");
                        navigate("/business/agent");
                    },
                }
            );
        } else {
            saveMutation.mutate(payload, {
                onSuccess: () => {
                    reset();
                    localStorage.removeItem("formData");
                    localStorage.removeItem("currentStep");
                    navigate("/business/agent");
                },
            });
        }

        localStorage.removeItem("formData");
        localStorage.removeItem("currentStep");
    };

    const goToNextStep = () => {
        saveFormDataToLocalStorage();
        setStep((prevStep) => {
            const newStep = prevStep + 1;
            localStorage.setItem("currentStep", newStep);
            return newStep;
        });
        if (props?.divref?.current) {
            props?.divref?.current.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const goToPreviousStep = () => {
        saveFormDataToLocalStorage();
        setStep((prevStep) => {
            const newStep = prevStep - 1;
            localStorage.setItem("currentStep", newStep);
            return newStep;
        });
        if (props?.divref?.current) {
            props?.divref?.current.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    // ── Step labels for the progress bar ──────────────────────────────────────
    const STEPS = ["Details", "Addresses", "Log Entry", "Summary"];

    return (
        <div className="d-flex flex-column flex-grow-1 h-100 overflow-hidden">
            {/* ── Step Progress Bar ── */}
            <div className="flex-shrink-0 mb-2 mb-sm-3 px-1 px-sm-3">
                <div className="d-flex justify-content-center">
                    <div className="d-flex align-items-center justify-content-between gap-0 w-100" style={{ maxWidth: "500px" }}>
                        {STEPS.map((label, idx) => {
                            const stepNum = idx + 1;
                            const isCompleted = step > stepNum;
                            const isCurrent = step === stepNum;
                            return (
                                <React.Fragment key={stepNum}>
                                    <div className="d-flex flex-column align-items-center text-center" style={{ flex: "0 0 auto", minWidth: "48px" }}>
                                        <div
                                            className="d-flex align-items-center justify-content-center rounded-circle fw-bold text-white shadow-sm"
                                            style={{
                                                width: "24px", height: "24px", fontSize: "10.5px",
                                                backgroundColor: isCompleted ? "#0A74C9" : isCurrent ? "#1A56DB" : "#dee2e6",
                                                color: isCompleted || isCurrent ? "#fff" : "#6c757d",
                                                transition: "background-color 0.3s ease",
                                            }}
                                        >
                                            {isCompleted ? "✓" : stepNum}
                                        </div>
                                        <span className="mt-1 fw-semibold text-uppercase"
                                            style={{
                                                color: isCurrent ? "#1A56DB" : isCompleted ? "#0A74C9" : "#9ca3af",
                                                fontSize: "9.5px",
                                                letterSpacing: "0.3px",
                                                lineHeight: "1.1"
                                            }}>
                                            {label}
                                        </span>
                                    </div>
                                    {idx < STEPS.length - 1 && (
                                        <div style={{
                                            flex: 1, height: "2px", marginBottom: "14px", minWidth: "12px",
                                            backgroundColor: step > stepNum ? "#0A74C9" : "#dee2e6",
                                            transition: "background-color 0.3s ease",
                                        }} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ── Page Header ── */}
            <div className="flex-shrink-0">
                <PageHeader title={isEditMode ? "Edit Packing List" : "New Packing List"} />
            </div>

            {/* ── Form Card (scrollable vertically) ── */}
            <div className="flex-grow-1 overflow-y-auto overflow-x-hidden d-flex flex-column">
                <form onSubmit={handleSubmit(onSubmit)} className="flex-grow-1 d-flex flex-column">
                    {step === 1 && (
                        <Step1Details
                            control={control}
                            customerNameList={customerNameList}
                            updateCustomerFields={updateCustomerFields}
                            goToNextStep={goToNextStep}
                            watch={watch}
                            isLoading={isLoading}
                            updateLoading={updateLoading}
                        />
                    )}

                    {step === 2 && (
                        <Step2Addresses
                            control={control}
                            partyName={partyName}
                            foundCustomer={foundCustomer}
                            selectedShippingIndex={selectedShippingIndex}
                            setSelectedShippingIndex={setSelectedShippingIndex}
                            handleAddressSelect={handleAddressSelect}
                            goToPreviousStep={goToPreviousStep}
                            goToNextStep={goToNextStep}
                            role={role}
                            isLoading={isLoading}
                            updateLoading={updateLoading}
                        />
                    )}

                    {step === 3 && (
                        <Step3TeakRounds
                            control={control}
                            watch={watch}
                            setValue={setValue}
                            userFields={userFields}
                            appendUser={appendUser}
                            removeUser={removeUser}
                            teakFields={teakFields}
                            appendTeak={appendTeak}
                            removeTeak={removeTeak}
                            lengthRefs={lengthRefs}
                            girthRefs={girthRefs}
                            fieldRefs={fieldRefs}
                            handleKeyPress={handleKeyPress}
                            handleEnterKeyPress={handleEnterKeyPress}
                            showModifyGirth={showModifyGirth}
                            setShowModifyGirth={setShowModifyGirth}
                            role={role}
                            isLoading={isLoading}
                            updateLoading={updateLoading}
                            goToPreviousStep={goToPreviousStep}
                            goToNextStep={goToNextStep}
                            calculateValues={calculateValues}
                            calculateAndUpdate={calculateAndUpdate}
                            aggregatedData={aggregatedData}
                            state={state}
                            isEditMode={isEditMode}
                        />
                    )}

                    {step === 4 && (
                        <Step4TeakSwans
                            control={control}
                            watch={watch}
                            formData={formData}
                            goToPreviousStep={goToPreviousStep}
                            isLoading={isLoading}
                            updateLoading={updateLoading}
                            step={step}
                            isEditMode={isEditMode}
                        />
                    )}
                </form>
            </div>
        </div>
    );
}
